import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { customerService } from '../../services/financeService';
import { buildCustomerRows } from '../../utils/exportUtils';
import CsvActions from '../common/CsvActions';
import AttachmentPanel from './AttachmentPanel';
import './Financial.css';

const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentTab, setCurrentTab] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    customerType: 'CUSTOMER',
    taxId: '',
    cnic: '',
    creditLimit: '',
    usdCreditLimit: '',
    creditLimitValidUntil: '',
    paymentTerms: '',
    contactPerson: '',
    notes: '',
    active: true
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.getAll();
      // API returns { success: true, data: [...] }
      const customerList = response.data || response || [];
      setCustomers(Array.isArray(customerList) ? customerList : []);
    } catch (err) {
      setError('Failed to load customers: ' + err.message);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (customer = null) => {
    if (customer) {
      setFormData({
        customerCode: customer.customerCode || '',
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        address: customer.address || '',
        city: customer.city || '',
        country: customer.country || '',
        customerType: customer.customerType || 'CUSTOMER',
        taxId: customer.taxNumber || customer.taxId || '',
        cnic: customer.cnic || '',
        creditLimit: customer.creditLimit || '',
        usdCreditLimit: customer.usdCreditLimit || '',
        creditLimitValidUntil: customer.creditLimitValidUntil || '',
        paymentTerms: customer.paymentTerms || '',
        contactPerson: customer.contactPerson || '',
        notes: customer.notes || '',
        active: customer.active !== false
      });
      setEditingId(customer.id);
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        customerType: 'CUSTOMER',
        taxId: '',
        cnic: '',
        creditLimit: '',
        usdCreditLimit: '',
        creditLimitValidUntil: '',
        paymentTerms: '',
        contactPerson: '',
        notes: '',
        active: true
      });
      setEditingId(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      customerType: 'CUSTOMER',
      taxId: '',
      cnic: '',
      creditLimit: '',
      usdCreditLimit: '',
      creditLimitValidUntil: '',
      paymentTerms: '',
      contactPerson: '',
      notes: '',
      active: true
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (!formData.name.trim()) {
      setError('Customer name is required');
      return;
    }

    // Sanitize payload: map frontend field names to DTO field names, and clean empty strings
    const payload = {
      name: formData.name.trim(),
      email: formData.email || null,
      phone: formData.phone || null,
      address: formData.address || null,
      city: formData.city || null,
      country: formData.country || null,
      taxNumber: formData.taxId || formData.taxNumber || null,
      cnic: formData.cnic ? formData.cnic.trim().replace(/-/g, '') : null,
      customerType: formData.customerType || 'CUSTOMER',
      creditLimit: formData.creditLimit !== '' && formData.creditLimit != null ? Number(formData.creditLimit) : null,
      usdCreditLimit: formData.usdCreditLimit !== '' && formData.usdCreditLimit != null ? Number(formData.usdCreditLimit) : null,
      creditLimitValidUntil: formData.creditLimitValidUntil || null,
      paymentTerms: formData.paymentTerms ? Number(formData.paymentTerms) : null,
      contactPerson: formData.contactPerson || null,
      notes: formData.notes || null,
      active: formData.active !== false,
    };

    try {
      // Label matches whatever Type was actually picked — used to always say "Customer" even
      // when creating a Vendor or Both, which read as if the wrong thing had saved. Fixed 2026-09-23.
      const typeLabel = payload.customerType === 'VENDOR' ? 'Vendor'
        : payload.customerType === 'BOTH' ? 'Customer/Vendor'
        : 'Customer';
      if (editingId) {
        await customerService.update(editingId, payload);
        setSuccess(`${typeLabel} updated`);
      } else {
        await customerService.create(payload);
        setSuccess(`${typeLabel} created`);
      }
      handleCloseDialog();
      fetchCustomers();
    } catch (err) {
      setError('Failed to save customer: ' + err.message);
    }
  };

  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });

  const handleDelete = (id, name) => {
    setDeleteDialog({ open: true, id, name });
  };

  const confirmDelete = async () => {
    try {
      await customerService.delete(deleteDialog.id);
        setSuccess('Customer deleted');
      setDeleteDialog({ open: false, id: null, name: '' });
      fetchCustomers();
    } catch (err) {
      setError('Failed to delete customer: ' + err.message);
      setDeleteDialog({ open: false, id: null, name: '' });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredCustomers = customers.filter(customer => {
    if (currentTab === 'all') return true;
    if (currentTab === 'customers') return customer.customerType === 'CUSTOMER';
    if (currentTab === 'vendors') return customer.customerType === 'VENDOR';
    if (currentTab === 'both') return customer.customerType === 'BOTH';
    return true;
  });

  const activeCustomers = filteredCustomers.filter(c => c.active !== false);
  const inactiveCustomers = filteredCustomers.filter(c => c.active === false);

  return (
    <Box className="financial-dashboard">
      <Box className="dashboard-header">
        <Box>
          <h2 className="financial-title">Customer & Vendor Management</h2>
          <p className="financial-subtitle">Manage your customers, vendors, and suppliers</p>
        </Box>
        <Box display="flex" gap={1}>
          {(() => { const { headers, matrix } = buildCustomerRows(filteredCustomers); return (
            <CsvActions filename="customers" headers={headers} matrix={matrix} />
          ); })()}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            New Customer/Vendor
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} style={{ marginBottom: '20px' }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} style={{ marginBottom: '20px' }}>{success}</Alert>}

      <Paper className="financial-card">
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} style={{ marginBottom: '20px' }}>
          <Tab label={`All (${customers.length})`} value="all" />
          <Tab label={`Customers (${customers.filter(c => c.customerType === 'CUSTOMER').length})`} value="customers" />
          <Tab label={`Vendors (${customers.filter(c => c.customerType === 'VENDOR').length})`} value="vendors" />
          <Tab label={`Both (${customers.filter(c => c.customerType === 'BOTH').length})`} value="both" />
        </Tabs>

        {loading ? (
          <Typography>Loading customers...</Typography>
        ) : (
          <>
            {activeCustomers.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
                  Active ({activeCustomers.length})
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>City / Country</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Credit Limit</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeCustomers.map((customer) => (
                        <TableRow key={customer.id}>
                          <TableCell>
                            <span style={{
                              fontFamily: 'var(--font-mono)',
                              fontSize: '12px',
                              background: 'var(--color-bg-tertiary)',
                              color: 'var(--color-text-secondary)',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}>
                              {customer.customerCode || '—'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {customer.customerType === 'VENDOR' ? (
                                <BusinessIcon style={{ marginRight: '10px', fontSize: 18, color: 'var(--color-text-tertiary)' }} />
                              ) : (
                                <PersonIcon style={{ marginRight: '10px', fontSize: 18, color: 'var(--color-text-tertiary)' }} />
                              )}
                              <Box>
                                <strong>{customer.name}</strong>
                                {customer.cnic && (
                                  <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-mono)' }}>
                                    CNIC: {customer.cnic.replace(/(\d{5})(\d{7})(\d{1})/, '$1-$2-$3')}
                                  </div>
                                )}
                                {customer.taxNumber && (
                                  <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>NTN: {customer.taxNumber}</div>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <span className={`type-badge type-${(customer.customerType || '').toLowerCase()}`}>{customer.customerType}</span>
                          </TableCell>
                          <TableCell>
                            {[customer.city, customer.country].filter(Boolean).join(', ') || '-'}
                          </TableCell>
                          <TableCell>{customer.phone || '-'}</TableCell>
                          <TableCell>
                            {customer.creditLimit ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span>PKR {Number(customer.creditLimit).toLocaleString()}</span>
                                {customer.creditLimitValidUntil && (() => {
                                  const exp = new Date(customer.creditLimitValidUntil);
                                  const now = new Date();
                                  const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
                                  if (daysLeft < 0) return <span style={{ fontSize: '0.7rem', background: 'var(--color-error-light)', color: 'var(--color-error)', borderRadius: 4, padding: '1px 5px' }}>Expired {exp.toLocaleDateString()}</span>;
                                  if (daysLeft <= 30) return <span style={{ fontSize: '0.7rem', background: 'var(--color-warning-light)', color: 'var(--color-warning-strong)', borderRadius: 4, padding: '1px 5px' }}>Expires {exp.toLocaleDateString()}</span>;
                                  return <span style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>Until {exp.toLocaleDateString()}</span>;
                                })()}
                              </div>
                            ) : null}
                            {customer.usdCreditLimit ? <div>USD {Number(customer.usdCreditLimit).toLocaleString()}</div> : null}
                            {!customer.creditLimit && !customer.usdCreditLimit ? '-' : null}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleOpenDialog(customer)} aria-label={`Edit ${customer.name}`} sx={{ color: 'var(--color-text-secondary)', '&:hover': { color: 'var(--color-text-primary)' } }}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDelete(customer.id, customer.name)} aria-label={`Delete ${customer.name}`} sx={{ color: 'var(--color-text-tertiary)', '&:hover': { color: 'var(--color-error)', bgcolor: 'var(--color-error-light)' } }}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {inactiveCustomers.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom style={{ marginTop: '20px', color: 'var(--color-text-tertiary)' }}>
                  Inactive ({inactiveCustomers.length})
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow style={{ opacity: 0.6 }}>
                        <TableCell>Code</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inactiveCustomers.map((customer) => (
                        <TableRow key={customer.id} style={{ opacity: 0.6 }}>
                          <TableCell>
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: 600 }}>
                              {customer.customerCode || '—'}
                            </span>
                          </TableCell>
                          <TableCell>{customer.name}</TableCell>
                          <TableCell>
                            <span className={`type-badge type-${(customer.customerType || '').toLowerCase()}`}>{customer.customerType}</span>
                          </TableCell>
                          <TableCell>{customer.phone || '-'}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleOpenDialog(customer)} aria-label={`Edit ${customer.name}`} sx={{ color: 'var(--color-text-secondary)' }}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {filteredCustomers.length === 0 && (
              <Box textAlign="center" py={4}>
                <Typography variant="body1" color="textSecondary">
                  No customers found. Click "New Customer/Vendor" to add one.
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Edit Customer/Vendor' : 'New Customer/Vendor'}
          {editingId && formData.customerCode && (
            <span style={{
              marginLeft: 12,
              fontFamily: 'var(--font-mono)',
              fontSize: '13px',
              background: formData.customerType === 'VENDOR' ? 'var(--color-warning-light)' : 'var(--color-info-light)',
              color: formData.customerType === 'VENDOR' ? 'var(--color-warning-strong)' : 'var(--color-primary)',
              padding: '2px 10px',
              borderRadius: '4px',
              fontWeight: 600,
              verticalAlign: 'middle',
            }}>
              {formData.customerCode}
            </span>
          )}
          {!editingId && (
            <span style={{ marginLeft: 12, fontSize: '12px', color: 'var(--color-text-secondary)', fontWeight: 400, verticalAlign: 'middle' }}>
              Code auto-generated on save
            </span>
          )}
        </DialogTitle>
        <DialogContent>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={1}>
            <TextField
              label="Name *"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Type"
              select
              value={formData.customerType}
              onChange={(e) => handleChange('customerType', e.target.value)}
              fullWidth
            >
              <MenuItem value="CUSTOMER">Customer</MenuItem>
              <MenuItem value="VENDOR">Vendor</MenuItem>
              <MenuItem value="BOTH">Both (Customer & Vendor)</MenuItem>
            </TextField>
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              fullWidth
            />
            <TextField
              label="Contact Person"
              value={formData.contactPerson}
              onChange={(e) => handleChange('contactPerson', e.target.value)}
              fullWidth
            />
            <TextField
              label="CNIC (13 digits — prevents duplicate entries)"
              value={formData.cnic}
              onChange={(e) => handleChange('cnic', e.target.value)}
              fullWidth
              placeholder="e.g. 3520212345671"
              inputProps={{ maxLength: 15 }}
              helperText="Enter 13-digit CNIC without dashes. System blocks duplicate CNICs."
            />
            <TextField
              label="Tax ID / NTN"
              value={formData.taxId}
              onChange={(e) => handleChange('taxId', e.target.value)}
              fullWidth
            />
            <TextField
              label="City"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              fullWidth
              placeholder="e.g., Karachi, Dubai"
            />
            <TextField
              label="Country"
              value={formData.country}
              onChange={(e) => handleChange('country', e.target.value)}
              fullWidth
              placeholder="e.g., Pakistan, UAE, UK"
            />
            <TextField
              label="Credit Limit (PKR)"
              type="number"
              value={formData.creditLimit}
              onChange={(e) => handleChange('creditLimit', e.target.value)}
              fullWidth
            />
            <TextField
              label="Credit Limit (USD)"
              type="number"
              value={formData.usdCreditLimit}
              onChange={(e) => handleChange('usdCreditLimit', e.target.value)}
              fullWidth
              placeholder="e.g., 100000"
              helperText="Leave blank if customer deals in PKR only"
            />
            <TextField
              label="Credit Limit Valid Until"
              type="date"
              value={formData.creditLimitValidUntil}
              onChange={(e) => handleChange('creditLimitValidUntil', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Payment Terms (days)"
              type="number"
              value={formData.paymentTerms}
              onChange={(e) => handleChange('paymentTerms', e.target.value)}
              fullWidth
              placeholder="e.g., 30"
            />
          </Box>
          <TextField
            label="Address"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            fullWidth
            multiline
            rows={2}
            style={{ marginTop: '16px' }}
          />
          <TextField
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            fullWidth
            multiline
            rows={3}
            style={{ marginTop: '16px' }}
          />
          <Box mt={2}>
            <label>
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => handleChange('active', e.target.checked)}
              />
              {' '}Active
            </label>
          </Box>
          {editingId && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Attachments <span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--color-text-secondary)' }}>(CNIC, business registration, etc.)</span>
              </Typography>
              <AttachmentPanel entityType="CUSTOMER" entityId={editingId} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog(d => ({ ...d, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete <strong>{deleteDialog.name}</strong>? This will deactivate the customer.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(d => ({ ...d, open: false }))}>Cancel</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomerManagement;
